import { COMMAND_DEFINITIONS } from '../commands/commandModel.js'
import { normalizeCommandBindingPreference } from './commandPreferenceStore.js'
import { normalizeCommandMacro } from './commandMacros.js'

const registry = new Map(COMMAND_DEFINITIONS.map(command => [command.id, command]))
const options = { commandRegistry: registry, registeredPromptIds: new Set(['navigation.symbol']) }
const legacyKey = 'erevis-command-customization-v1'
const empty = () => ({ bindings: [], macros: [] })
function parse(storage, key) {
  try { return JSON.parse(storage?.getItem(key) || 'null') } catch { return null }
}
function validate(value) {
  if (!value || !Array.isArray(value.bindings) || !Array.isArray(value.macros)) return null
  if (value.bindings.length > 512 || value.macros.length > 128) return null
  const bindings = value.bindings.map(row => normalizeCommandBindingPreference(row, options))
  const macros = value.macros.map(row => normalizeCommandMacro(row, options))
  return { bindings: bindings.filter(Boolean), macros: macros.filter(Boolean), invalidCount: [...bindings, ...macros].filter(row => !row).length }
}

// A controller belongs to exactly one project + authenticated subject. No email
// matching, ambient getUser fallback, or automatic adoption of unowned legacy data.
export function createCommandAccountSync({ userId, project, client, storage, onChange }) {
  const key = `erevis-command-account-v2:${encodeURIComponent(project)}:${userId}`
  const cached = userId ? parse(storage, key) : null
  const validCache = validate(cached?.document)
  const legacy = parse(storage, legacyKey)
  let document = validCache ? { bindings: validCache.bindings, macros: validCache.macros } : empty()
  let revision = Number.isSafeInteger(cached?.revision) && cached.revision >= 0 ? cached.revision : null
  let dirty = Boolean(cached?.dirty && revision !== null)
  let status = userId ? 'loading' : 'signed-out'
  let invalidCount = validCache?.invalidCount || (cached?.quarantined ? 1 : 0)
  const quarantinedCache = cached?.quarantined || (invalidCount ? cached?.document : null)
  let cacheFailed = false, disposed = false, busy = null, generation = 0
  let hydrated = false
  const legacyAvailable = Boolean(legacy && (legacy.bindings?.length || legacy.macros?.length))
  const get = () => ({ ...document, status, invalidCount, cacheFailed, legacyAvailable, canEdit: Boolean(userId && hydrated && status !== 'conflict' && !invalidCount), revision })
  function emit() {
    if (disposed) return
    if (userId) {
      try {
        storage?.setItem(key, JSON.stringify({ revision, dirty, document, quarantined: quarantinedCache }))
        cacheFailed = !storage
      } catch { cacheFailed = true }
    }
    onChange?.(get())
  }
  async function request(name, args = {}) {
    const abort = new AbortController()
    let timer
    try {
      const request = client.rpc(name, { expected_user_id: userId, ...args })
      const response = typeof request.abortSignal === 'function' ? request.abortSignal(abort.signal) : request
      const { data, error } = await Promise.race([response, new Promise((_, reject) => {
        timer = setTimeout(() => { abort.abort(); reject(new Error('Account sync timed out')) }, 15000)
      })])
      if (error || !data) throw new Error('Command account synchronization unavailable')
      return data
    } finally { clearTimeout(timer) }
  }
  async function save() {
    while (dirty && !disposed && !invalidCount && status !== 'conflict') {
      const sentGeneration = generation
      const sent = document
      status = 'saving'; emit()
      const result = await request('save_command_customization', { expected_revision: revision, customization: sent })
      if (disposed) return false
      if (result.conflict) { status = 'conflict'; emit(); return false }
      if (!Number.isSafeInteger(result.revision) || result.revision <= revision) throw Error('Invalid revision')
      revision = result.revision
      dirty = generation !== sentGeneration
      emit()
    }
    if (!disposed) { status = invalidCount ? 'invalid' : 'synced'; emit() }
    return !dirty
  }
  function serialize(action) {
    if (busy) return busy
    busy = (async () => {
      try { return await action() } catch {
        if (!disposed) { status = 'offline'; emit() }
        return false
      }
    })().finally(() => { busy = null })
    return busy
  }
  function refresh() {
    if (!userId || disposed || status === 'conflict') return Promise.resolve(false)
    return serialize(async () => {
      if (dirty) { hydrated = true; return save() }
      const data = await request('read_command_customization')
      if (disposed) return false
      // An edit during a background read must be saved against the last observed
      // revision, never replaced or rebased automatically onto the new response.
      if (dirty) return save()
      const valid = validate(data)
      if (!valid || !Number.isSafeInteger(data.revision) || data.revision < 0) throw Error('Invalid account customization')
      if (revision !== data.revision || !hydrated) document = { bindings: valid.bindings, macros: valid.macros }
      invalidCount = valid.invalidCount
      revision = data.revision; hydrated = true
      status = invalidCount ? 'invalid' : 'synced'; emit()
      return true
    })
  }
  async function edit(update) {
    if (!get().canEdit || disposed) return false
    const next = validate(update(document))
    if (!next || next.invalidCount) return false
    document = { bindings: next.bindings, macros: next.macros }
    dirty = true; generation += 1; emit()
    // If a read/save is already running, it observes dirty/generation before
    // completing. All mutations are serialized through the same CAS operation.
    return serialize(save)
  }
  async function useAccount() {
    if (disposed || busy) return false
    // Preserve discarded drafts for recovery; never remove the legacy key.
    try { storage?.setItem(`${key}:discarded`, JSON.stringify({ revision, document })) } catch { cacheFailed = true; emit(); return false }
    dirty = false; invalidCount = 0; status = 'loading'
    return refresh()
  }
  async function importDocument(value) {
    if (!get().canEdit || busy || dirty) return false
    const valid = validate(value)
    if (!valid || valid.invalidCount) { invalidCount = valid?.invalidCount || 1; status = 'invalid'; emit(); return false }
    // Explicit operator choice; CAS still refuses a concurrent device's change.
    return edit(() => valid)
  }
  const importLegacy = () => importDocument(legacy)
  return { get, refresh, edit, useAccount, importLegacy, importDocument, activate: () => { disposed = false }, dispose: () => { disposed = true } }
}
